import {Component, ElementRef, HostListener, inject, input, output} from '@angular/core';
import {ImagesService} from '../../../../services/general/images.service';
import {Admin} from '../../../../interfaces/responses/admin-response.interfaces';
import {AdminService} from '../../../../services/admin/admin.service';
import {ToastService} from '../../../../services/general/toast.service';
import {ConfirmationDialogComponent} from '../../../shared/confirmation-dialog/confirmation-dialog.component';
import {CommonModule, DatePipe} from '@angular/common';

@Component({
  selector: 'app-users-card',
  imports: [CommonModule, DatePipe, ConfirmationDialogComponent],
  templateUrl: './users-card.component.html',
  styleUrl: './users-card.component.scss'
})
export class UsersCardComponent {
  _images = inject(ImagesService);
  adminService = inject(AdminService);
  _toast = inject(ToastService);
  _eref = inject(ElementRef);

  admin = input<Admin>();
  isActionClick: boolean = false;
  showDeleteDialog: boolean = false;
  showStatusDialog: boolean = false;

  editUserEvent = output<Admin>();
  deleteUserEvent = output<void>();
  toggleStatusEvent = output<void>();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this._eref.nativeElement.contains(event.target)) {
      this.isActionClick = false;
    }
  }

  getFullName(): string {
    const admin = this.admin();
    if (!admin) return '';
    const parts = [admin.firstName, admin.lastName];
    if (admin.otherName) {
      parts.splice(1, 0, admin.otherName);
    }
    return parts.join(' ');
  }

  onActionClick() {
    this.isActionClick = !this.isActionClick;
  }

  get dropdownOptions(): any[] {
    const admin = this.admin();
    const isActive = admin?.isActive ?? false;
    
    const options = [
      {
        title: 'Edit',
        icon: this._images.editIcon
      },
      {
        title: isActive ? 'Deactivate' : 'Activate',
        icon: isActive ? this._images.deniedIcon : this._images.checkIcon
      },
      {
        title: 'Delete',
        icon: this._images.deleteIcon
      },
    ];
    
    return options;
  }

  onOptionClick(title: string) {
    switch(title) {
      case 'Edit':
        this.editUser();
        this.isActionClick = false;
        break;
      case 'Activate':
      case 'Deactivate':
        this.showStatusConfirmation();
        this.isActionClick = false;
        break;
      case 'Delete':
        this.showDeleteConfirmation();
        this.isActionClick = false;
        break;
      default:
        return;
    }
  }

  editUser() {
    const admin = this.admin();
    if (admin) {
      this.editUserEvent.emit(admin);
    }
  }

  showDeleteConfirmation() {
    this.showDeleteDialog = true;
  }

  cancelDelete() {
    this.showDeleteDialog = false;
  }

  confirmDelete() {
    const id = this.admin()?.id;
    if (!id) {
      console.error('Admin ID is undefined. Cannot delete admin.');
      return;
    }

    this.adminService.deleteAdmin(id).subscribe({
      next: () => {
        this._toast.success('Admin deleted successfully');
        this.showDeleteDialog = false;
        this.deleteUserEvent.emit();
      },
      error: (error) => {
        console.error('Error deleting admin:', error);
        this._toast.error(error?.message || 'Error deleting admin');
        this.showDeleteDialog = false;
      }
    });
  }

  showStatusConfirmation() {
    this.showStatusDialog = true;
  }

  cancelStatusToggle() {
    this.showStatusDialog = false;
  }

  confirmStatusToggle() {
    const id = this.admin()?.id;
    if (!id) {
      console.error('Admin ID is undefined. Cannot toggle admin status.');
      this.showStatusDialog = false;
      return;
    }

    const admin = this.admin();
    const currentStatus = admin?.isActive ?? false;
    const action = currentStatus ? 'deactivated' : 'activated';

    this.adminService.toggleStatus(id).subscribe({
      next: () => {
        this._toast.success(`Admin ${action} successfully`);
        this.showStatusDialog = false;
        this.toggleStatusEvent.emit();
      },
      error: (error) => {
        console.error('Error toggling admin status:', error);
        this._toast.error(error?.message || `Error ${action} admin`);
        this.showStatusDialog = false;
      }
    });
  }
}
